import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useOutletContext } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query, startAfter, Timestamp, where } from 'firebase/firestore';
import { db } from '../firebase';
import { LoadingState, ErrorState, EmptyState } from '../components/States';

const ADMIN_UIDS = ['cXXmwFwOebRnN0h6rPb1E4ASRIt1', 'WosYCVaIbrXGFLST7sjEQc1WQaU2'];

const PAGE_SIZE = 20;

const formatDate = (value) => {
    if (!value) return '-';
    const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleString('th-TH');
};

const AuditLog = () => {
    const { user } = useOutletContext();
    const [isAuthorized, setIsAuthorized] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [logs, setLogs] = useState([]);

    const [searchText, setSearchText] = useState('');
    const [actionFilter, setActionFilter] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [page, setPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [pageCursors, setPageCursors] = useState({ 1: null });
    const [lastVisibleDoc, setLastVisibleDoc] = useState(null);

    useEffect(() => {
        if (!user || !user.uid) return;
        setIsAuthorized(ADMIN_UIDS.includes(user.uid));
    }, [user]);

    const loadPage = useCallback(
        async (pageNumber, cursorDoc = null) => {
            setIsPageLoading(true);
            setError('');

            try {
                const constraints = [orderBy('createdAt', 'desc')];

                if (dateFrom) {
                    constraints.push(where('createdAt', '>=', Timestamp.fromDate(new Date(`${dateFrom}T00:00:00`))));
                }
                if (dateTo) {
                    constraints.push(where('createdAt', '<=', Timestamp.fromDate(new Date(`${dateTo}T23:59:59.999`))));
                }
                if (cursorDoc) {
                    constraints.push(startAfter(cursorDoc));
                }
                constraints.push(limit(PAGE_SIZE + 1));

                const pageQuery = query(collection(db, 'audit_logs'), ...constraints);
                const snapshot = await getDocs(pageQuery);
                const docs = snapshot.docs;
                const hasNext = docs.length > PAGE_SIZE;
                const pageDocs = hasNext ? docs.slice(0, PAGE_SIZE) : docs;
                const nextCursorDoc = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;

                setLogs(pageDocs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
                setHasMorePages(hasNext);
                setPage(pageNumber);
                setLastVisibleDoc(nextCursorDoc);
                setPageCursors((prev) => {
                    const next = { ...prev, [pageNumber]: cursorDoc || null };
                    if (hasNext && nextCursorDoc) {
                        next[pageNumber + 1] = nextCursorDoc;
                    }
                    return next;
                });
            } catch (err) {
                console.error('Error loading audit logs:', err);
                setError('ไม่สามารถโหลดประวัติการทำรายการได้');
            } finally {
                setLoading(false);
                setIsPageLoading(false);
            }
        },
        [dateFrom, dateTo]
    );

    useEffect(() => {
        if (isAuthorized !== true) {
            if (isAuthorized === false) {
                setLoading(false);
            }
            return;
        }
        setPageCursors({ 1: null });
        setLastVisibleDoc(null);
        loadPage(1, null);
    }, [isAuthorized, loadPage]);

    const filteredLogs = useMemo(() => {
        if (!searchText && !actionFilter) return logs;
        const q = searchText.trim().toLowerCase();
        return logs.filter((log) => {
            const action = (log.action || '').toLowerCase();
            const actorEmail = (log.actorEmail || '').toLowerCase();
            const targetId = (log.targetId || '').toLowerCase();
            const metadata = JSON.stringify(log.metadata || {}).toLowerCase();

            if (actionFilter && action !== actionFilter.toLowerCase()) return false;
            if (!q) return true;
            return action.includes(q) || actorEmail.includes(q) || targetId.includes(q) || metadata.includes(q);
        });
    }, [logs, searchText, actionFilter]);

    const actionOptions = useMemo(() => {
        const set = new Set(logs.map((log) => log.action).filter(Boolean));
        return Array.from(set).sort();
    }, [logs]);

    const handleNextPage = () => {
        if (!hasMorePages || isPageLoading) return;
        const nextPage = page + 1;
        const cursor = pageCursors[nextPage] || lastVisibleDoc || null;
        loadPage(nextPage, cursor);
    };

    const handlePrevPage = () => {
        if (page <= 1 || isPageLoading) return;
        const prevPage = page - 1;
        const cursor = pageCursors[prevPage] || null;
        loadPage(prevPage, cursor);
    };

    const knownPages = Object.keys(pageCursors)
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0);
    if (!knownPages.includes(page)) knownPages.push(page);
    if (hasMorePages && !knownPages.includes(page + 1)) knownPages.push(page + 1);
    const visiblePageNumbers = Array.from(new Set(knownPages))
        .sort((a, b) => a - b)
        .filter((value) => value >= Math.max(1, page - 3) && value <= page + 3);

    const handleGotoPage = (targetPage) => {
        if (targetPage === page || isPageLoading) return;
        if (targetPage === page + 1) {
            handleNextPage();
            return;
        }
        if (targetPage === page - 1) {
            handlePrevPage();
            return;
        }
        if (!(targetPage in pageCursors)) return;
        loadPage(targetPage, pageCursors[targetPage] || null);
    };

    if (isAuthorized === null || loading) {
        return <LoadingState message="กำลังโหลด Audit Log..." />;
    }

    if (isAuthorized === false) {
        return <Navigate to="/" replace />;
    }

    if (error) {
        return <ErrorState message={error} />;
    }

    return (
        <main className="min-h-screen bg-slate-50">
            <div className="container mx-auto p-4 sm:p-6 lg:p-8">
                <div className="mb-4">
                    <Link to="/" className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
                        กลับหน้าแดชบอร์ด
                    </Link>
                </div>

                <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <h1 className="text-2xl font-bold text-slate-800 sm:text-3xl">Audit Log</h1>
                    <p className="mt-2 text-sm text-slate-600">ประวัติการแก้ไข/ลบลิงก์ และงานสำคัญย้อนหลัง</p>

                    <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-5">
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                        />
                        <select
                            value={actionFilter}
                            onChange={(e) => setActionFilter(e.target.value)}
                            className="rounded-md border border-slate-300 px-3 py-2 text-sm"
                        >
                            <option value="">ทุก Action</option>
                            {actionOptions.map((action) => (
                                <option key={action} value={action}>
                                    {action}
                                </option>
                            ))}
                        </select>
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="ค้นหา actor/target/metadata"
                            className="rounded-md border border-slate-300 px-3 py-2 text-sm md:col-span-2"
                        />
                    </div>
                </section>

                <section className="mt-5 rounded-2xl border border-slate-200 bg-white shadow-sm">
                    {filteredLogs.length === 0 ? (
                        <div className="p-6">
                            <EmptyState title="ไม่พบข้อมูล Audit Log" description="ลองปรับตัวกรองหรือช่วงวันที่ใหม่" />
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[980px] text-left text-sm">
                                <thead className="border-b border-slate-200 bg-slate-50">
                                    <tr>
                                        <th className="p-3 font-semibold text-slate-700">วันเวลา</th>
                                        <th className="p-3 font-semibold text-slate-700">Action</th>
                                        <th className="p-3 font-semibold text-slate-700">ผู้ดำเนินการ</th>
                                        <th className="p-3 font-semibold text-slate-700">Target</th>
                                        <th className="p-3 font-semibold text-slate-700">รายละเอียด</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredLogs.map((log) => (
                                        <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                                            <td className="whitespace-nowrap p-3">{formatDate(log.createdAt)}</td>
                                            <td className="p-3">
                                                <span className="rounded-full bg-sky-100 px-2 py-1 text-xs font-semibold text-sky-700">{log.action || '-'}</span>
                                            </td>
                                            <td className="p-3 text-slate-700">{log.actorEmail || log.actorUid || '-'}</td>
                                            <td className="p-3 font-mono text-xs text-slate-600">{log.targetId || '-'}</td>
                                            <td className="p-3 text-slate-600">{JSON.stringify(log.metadata || {})}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {(page > 1 || hasMorePages) && (
                        <div className="flex flex-col gap-3 border-t border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                            <div className="text-sm text-slate-600">หน้า {page}</div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handlePrevPage}
                                    disabled={page === 1 || isPageLoading}
                                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                                >
                                    ก่อนหน้า
                                </button>
                                <div className="flex items-center gap-1 overflow-x-auto">
                                    {visiblePageNumbers.map((pageNumber) => (
                                        <button
                                            key={pageNumber}
                                            type="button"
                                            onClick={() => handleGotoPage(pageNumber)}
                                            disabled={isPageLoading || pageNumber === page || (!(pageNumber in pageCursors) && pageNumber !== page + 1)}
                                            className={`min-w-9 rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                                                pageNumber === page
                                                    ? 'border-blue-600 bg-blue-600 text-white'
                                                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50'
                                            }`}
                                        >
                                            {pageNumber}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={handleNextPage}
                                    disabled={!hasMorePages || isPageLoading}
                                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50"
                                >
                                    ถัดไป
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            </div>
        </main>
    );
};

export default AuditLog;
